import React, { useRef, useMemo, Suspense, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useTexture, Environment, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';
import { HolographicMaterial, GlitchMaterial, BlurredMaterial } from './ShaderMaterials';
import { EnhancedLighting, GalleryTransition } from './TransitionEffects';
import { GALLERY_CONFIG, ADAPTIVE_COLORS } from '../constants/galleryConfig';
import ContactForm from './ContactForm';
// Lazy load the heavy modal component
const EnhancedArtworkModal = React.lazy(() => import('./EnhancedArtworkModal'));

// Modern AI-Powered Dynamic Particle System
const AIParticleField = ({ mousePosition, selectedArtwork }) => {
  const particlesRef = useRef();
  const particleCount = 200;
  
  // Create particle positions and velocities
  const particles = useMemo(() => {
    const positions = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    
    for (let i = 0; i < particleCount; i++) {
      // Random positions in 3D space
      positions[i * 3] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
      
      // Random velocities
      velocities[i * 3] = (Math.random() - 0.5) * 0.02;
      velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.02;
      velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.02;
      
      // Minimal white/gray colors
      const brightness = Math.random() * 0.3 + 0.7; // 0.7 to 1.0 (gray to white)
      colors[i * 3] = brightness;     // R
      colors[i * 3 + 1] = brightness; // G  
      colors[i * 3 + 2] = brightness; // B
    }
    
    return { positions, velocities, colors };
  }, []);

  useFrame((state) => {
    if (!particlesRef.current) return;
    
    const positions = particlesRef.current.geometry.attributes.position.array;
    const colors = particlesRef.current.geometry.attributes.color.array;
    const time = state.clock.elapsedTime;
    
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      
      // AI-like behavior: particles attracted to mouse and selected artwork
      let targetX = 0, targetY = 0, targetZ = 0;
      
      if (mousePosition) {
        targetX = (mousePosition.x - 0.5) * 10;
        targetY = -(mousePosition.y - 0.5) * 5;
      }
      
      if (selectedArtwork) {
        // Particles swarm around selected artwork
        const angle = (i / particleCount) * Math.PI * 2 + time * 0.5;
        targetX = Math.cos(angle) * 3;
        targetZ = Math.sin(angle) * 3;
        targetY = Math.sin(time + i * 0.1) * 2;
      }
      
      // Smooth movement towards target
      positions[i3] += (targetX - positions[i3]) * 0.02;
      positions[i3 + 1] += (targetY - positions[i3 + 1]) * 0.02;
      positions[i3 + 2] += (targetZ - positions[i3 + 2]) * 0.02;
      
      // Add some organic movement
      positions[i3] += Math.sin(time * 2 + i * 0.1) * 0.01;
      positions[i3 + 1] += Math.cos(time * 1.5 + i * 0.15) * 0.01;
      positions[i3 + 2] += Math.sin(time * 1.8 + i * 0.12) * 0.01;
      
      // Minimal white/gray shifting
      const baseIntensity = selectedArtwork ? 1.0 : 0.7;
      const variation = Math.sin(time + i * 0.1) * 0.1; // Subtle variation
      const brightness = baseIntensity + variation;
      colors[i3] = brightness;     // R
      colors[i3 + 1] = brightness; // G
      colors[i3 + 2] = brightness; // B
    }
    
    particlesRef.current.geometry.attributes.position.needsUpdate = true;
    particlesRef.current.geometry.attributes.color.needsUpdate = true;
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={particles.positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={particleCount}
          array={particles.colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        vertexColors
        transparent
        opacity={0.8}
        blending={THREE.AdditiveBlending}
        sizeAttenuation={true}
      />
    </points>
  );
};

// Modern Holographic UI Elements
const HolographicUI = ({ selectedArtwork }) => {
  const ringRef = useRef();
  
  useFrame((state) => {
    if (ringRef.current) {
      ringRef.current.rotation.z = state.clock.elapsedTime * 0.5;
      ringRef.current.material.opacity = Math.sin(state.clock.elapsedTime * 2) * 0.3 + 0.7;
    }
  });

  if (!selectedArtwork) return null;

  return (
    <group position={[0, 0, 0]}>
      {/* Holographic selection ring */}
      <mesh ref={ringRef}>
        <ringGeometry args={[6, 6.2, 32]} />
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={0.4}
          side={THREE.DoubleSide}
        />
      </mesh>
      
      {/* Floating info particles */}
      <mesh position={[0, 3, 0]}>
        <sphereGeometry args={[0.1]} />
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={0.9}
        />
      </mesh>
    </group>
  );
};

// Enhanced artwork component with shader effects
const Artwork = React.memo(({ position, rotation, imageSrc, onClick, isSelected, cameraPosition, selectedArtwork }) => {
  const meshRef = useRef();
  const groupRef = useRef();
  const [isHovered, setIsHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const texture = useTexture(imageSrc);
  
  // Calculate distance from camera for proximity scaling
  const distanceFromCamera = useMemo(() => {
    if (!cameraPosition) return 8;
    const artworkPos = new THREE.Vector3(...position);
    const camPos = new THREE.Vector3(...cameraPosition);
    return artworkPos.distanceTo(camPos);
  }, [position, cameraPosition]);
  
  // Calculate blur amount based on selection
  const blurAmount = useMemo(() => {
    if (!selectedArtwork) return 0;
    return isSelected ? 0 : GALLERY_CONFIG.BLUR_AMOUNT;
  }, [isSelected, selectedArtwork]);
  
  // Configure texture for perfect circular mapping
  useEffect(() => {
    if (texture) {
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.center.set(0.5, 0.5);
      texture.needsUpdate = true;
    }
  }, [texture]);
  
  useFrame((state) => {
    if (meshRef.current && groupRef.current) {
      const time = state.clock.elapsedTime;
      
      // Enhanced floating animation with rotation
      meshRef.current.position.y = position[1] + Math.sin(time + position[0]) * GALLERY_CONFIG.FLOATING_AMPLITUDE;
      
      // Subtle rotation when hovered
      if (isHovered) {
        meshRef.current.rotation.z = Math.sin(time * GALLERY_CONFIG.ROTATION_SPEED) * GALLERY_CONFIG.ROTATION_AMPLITUDE;
      }
      
      // Proximity scaling - artworks grow as camera gets closer
      const { BASE_SCALE, MAX_SCALE, MIN_DISTANCE, MAX_DISTANCE, SELECTED_BOOST, LERP_SPEED } = GALLERY_CONFIG.PROXIMITY_SCALING;
      
      const normalizedDistance = Math.max(0, Math.min(1, (distanceFromCamera - MIN_DISTANCE) / (MAX_DISTANCE - MIN_DISTANCE)));
      const proximityScale = BASE_SCALE + (MAX_SCALE - BASE_SCALE) * (1 - normalizedDistance);
      
      // Smooth scaling transition
      const targetScale = isSelected ? proximityScale * SELECTED_BOOST : proximityScale;
      groupRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), LERP_SPEED);
      
      // Depth-of-field blur effect
      if (meshRef.current.material && meshRef.current.material.uniforms) {
        const currentBlur = meshRef.current.material.uniforms.blurAmount?.value || 0;
        const targetBlur = blurAmount;
        const newBlur = THREE.MathUtils.lerp(currentBlur, targetBlur, GALLERY_CONFIG.BLUR_LERP_SPEED);
        if (meshRef.current.material.uniforms.blurAmount) {
          meshRef.current.material.uniforms.blurAmount.value = newBlur;
        }
      }
    }
  });

  return (
    <group ref={groupRef} position={position} rotation={rotation}>
      <mesh
        ref={meshRef}
        onClick={onClick}
        onPointerOver={(e) => {
          e.stopPropagation();
          setIsHovered(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          setIsHovered(false);
          document.body.style.cursor = 'auto';
        }}
      >
        <circleGeometry args={[GALLERY_CONFIG.ARTWORK_SIZE, GALLERY_CONFIG.ARTWORK_SEGMENTS]} />
        <meshStandardMaterial 
          map={texture} 
          transparent
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
});

// Advanced lighting system with IBL and adaptive features
const AdvancedLightingSystem = ({ selectedArtwork, cameraPosition }) => {
  const spotlightRef = useRef();
  const adaptiveLightRef = useRef();
  const rimLightRef = useRef();
  
  // Extract dominant color from selected artwork
  const adaptiveColor = useMemo(() => {
    if (!selectedArtwork) return '#ffffff';
    return ADAPTIVE_COLORS[selectedArtwork.id % ADAPTIVE_COLORS.length];
  }, [selectedArtwork]);
  
  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    // Adaptive spotlight that follows selected artwork
    if (spotlightRef.current && selectedArtwork) {
      const artworkIndex = selectedArtwork.id - 1;
      const angle = (artworkIndex / 25) * Math.PI * 2; // Assuming ~25 artworks
      const targetX = Math.cos(angle) * 5;
      const targetZ = Math.sin(angle) * 5;
      
      // Smooth spotlight movement
      spotlightRef.current.target.position.lerp(
        new THREE.Vector3(targetX, 0, targetZ), 
        0.05
      );
      spotlightRef.current.intensity = 0.8 + Math.sin(time * 2) * 0.2;
    }
    
    // Adaptive ambient light based on artwork colors
    if (adaptiveLightRef.current) {
      const color = new THREE.Color(adaptiveColor);
      adaptiveLightRef.current.color.lerp(color, 0.02);
      adaptiveLightRef.current.intensity = 0.3 + Math.sin(time * 0.5) * 0.1;
    }
    
    // Rim lighting for depth
    if (rimLightRef.current) {
      rimLightRef.current.position.x = Math.cos(time * 0.3) * 8;
      rimLightRef.current.position.z = Math.sin(time * 0.3) * 8;
      rimLightRef.current.intensity = 0.4 + Math.sin(time * 0.8) * 0.1;
    }
  });
  
  return (
    <>
      {/* Environment-based lighting (IBL) */}
      <Environment preset="city" background={false} />
      
      {/* Main ambient light */}
      <ambientLight intensity={0.2} />
      
      {/* Adaptive colored ambient */}
      <ambientLight 
        ref={adaptiveLightRef}
        color={adaptiveColor}
        intensity={0.3}
      />
      
      {/* Dynamic spotlight for selected artwork */}
      <spotLight
        ref={spotlightRef}
        position={[0, 8, 0]}
        angle={0.3}
        penumbra={0.5}
        intensity={0.8}
        color={adaptiveColor}
        castShadow
        shadow-mapSize={[1024, 1024]}
        shadow-bias={-0.0001}
      />
      
      {/* Rim lighting for depth */}
      <directionalLight
        ref={rimLightRef}
        position={[8, 4, 8]}
        intensity={0.4}
        color="#ffffff"
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-far={50}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
      />
      
      {/* Fill light */}
      <pointLight
        position={[-5, 2, -5]}
        intensity={0.3}
        color="#4a90e2"
        distance={20}
      />
    </>
  );
};

// Fullscreen Modal Component
const FullscreenModal = ({ artwork, isOpen, onClose }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsLoaded(false);
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  if (!isOpen || !artwork) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center transition-all duration-500 ease-out"
      style={{
        backdropFilter: 'blur(10px)',
        animation: isOpen ? 'modalFadeIn 0.5s ease-out' : 'modalFadeOut 0.3s ease-in'
      }}
      onClick={onClose}
    >
      {/* Close button */}
      <button 
        className="absolute top-6 right-6 text-white text-2xl hover:text-gray-300 transition-colors z-10"
        onClick={onClose}
      >
        ✕
      </button>
      
      {/* Loading indicator */}
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      
      {/* Artwork container */}
      <div 
        className="relative transition-all duration-700 ease-out transform"
        style={{
          transform: isLoaded ? 'scale(1) rotate(0deg)' : 'scale(0.8) rotate(-5deg)',
          opacity: isLoaded ? 1 : 0,
          width: artwork.width ? `${artwork.width}px` : 'auto',
          height: artwork.height ? `${artwork.height}px` : 'auto',
          maxWidth: '95vw',
          maxHeight: '95vh'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {artwork.type === 'image' ? (
          <img
            src={artwork.src}
            alt={artwork.title || 'Artwork'}
            className="rounded-lg shadow-2xl"
            style={{
              filter: 'drop-shadow(0 25px 50px rgba(255, 255, 255, 0.1))',
              width: artwork.width ? `${artwork.width}px` : 'auto',
              height: artwork.height ? `${artwork.height}px` : 'auto',
              maxWidth: '95vw',
              maxHeight: '95vh',
              objectFit: 'contain'
            }}
            onLoad={() => setIsLoaded(true)}
          />
        ) : (
          <video
            src={artwork.src}
            className="rounded-lg shadow-2xl"
            style={{
              filter: 'drop-shadow(0 25px 50px rgba(255, 255, 255, 0.1))',
              width: artwork.width ? `${artwork.width}px` : 'auto',
              height: artwork.height ? `${artwork.height}px` : 'auto',
              maxWidth: '95vw',
              maxHeight: '95vh',
              objectFit: 'contain'
            }}
            controls
            autoPlay
            muted
            onLoadedData={() => setIsLoaded(true)}
          />
        )}
        
        {/* Artwork info overlay */}
        {isLoaded && artwork.title && (
          <div 
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6 rounded-b-lg transition-all duration-500"
            style={{
              transform: isLoaded ? 'translateY(0)' : 'translateY(100%)'
            }}
          >
            <h3 className="text-white text-xl font-bold mb-2">{artwork.title}</h3>
            {artwork.medium && <p className="text-gray-300 text-sm mb-1">{artwork.medium}</p>}
            {artwork.year && <p className="text-gray-400 text-sm mb-2">{artwork.year}</p>}
            {artwork.description && <p className="text-gray-200 text-sm">{artwork.description}</p>}
          </div>
        )}
      </div>
      
      <style jsx>{`
        @keyframes modalFadeIn {
          from {
            opacity: 0;
            backdrop-filter: blur(0px);
          }
          to {
            opacity: 1;
            backdrop-filter: blur(10px);
          }
        }
        
        @keyframes modalFadeOut {
          from {
            opacity: 1;
            backdrop-filter: blur(10px);
          }
          to {
            opacity: 0;
            backdrop-filter: blur(0px);
          }
        }
      `}</style>
    </div>
  );
};

// Main 3D Gallery Component
const VirtualGallery = ({ mediaItems, onArtworkSelect }) => {
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [fullscreenArtwork, setFullscreenArtwork] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0.5, y: 0.5 });

  // Modern mouse tracking for AI particles
  useEffect(() => {
    const handleMouseMove = (event) => {
      setMousePosition({
        x: event.clientX / window.innerWidth,
        y: event.clientY / window.innerHeight
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  const handleArtworkClick = (artwork) => {
    setFullscreenArtwork(artwork);
    setIsModalOpen(true);
    // Still call the original onArtworkSelect for URL updates
    if (onArtworkSelect) {
      onArtworkSelect(artwork);
    }
  };

  const handleArtworkNavigate = (artwork) => {
    setFullscreenArtwork(artwork);
    if (onArtworkSelect) {
      onArtworkSelect(artwork);
    }
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setFullscreenArtwork(null), 300);
  };
  
  // Arrange artworks in a circle
  const artworkPositions = useMemo(() => {
    const imageItems = mediaItems.filter(item => item.type === 'image');
    
    return imageItems.map((item, index) => {
      const angle = (index / imageItems.length) * Math.PI * 2;
      const x = Math.cos(angle) * GALLERY_CONFIG.ARTWORK_RADIUS;
      const z = Math.sin(angle) * GALLERY_CONFIG.ARTWORK_RADIUS;
      
      return {
        position: [x, 0, z],
        rotation: [0, -angle + Math.PI, 0] // Face inward toward center
      };
    });
  }, [mediaItems]);

  return (
    <div className="fixed inset-0 w-full h-full bg-black overflow-hidden">
      <Canvas
        camera={{ position: GALLERY_CONFIG.CAMERA_POSITION, fov: GALLERY_CONFIG.CAMERA_FOV }}
        style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: '#000000',
          display: 'block'
        }}
        shadows
        gl={{ 
          antialias: true,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: GALLERY_CONFIG.TONE_MAPPING_EXPOSURE
        }}
      >
        
        {/* Modern AI-Powered Particle Field */}
        <AIParticleField 
          mousePosition={mousePosition}
          selectedArtwork={selectedArtwork}
        />
        
        {/* Holographic UI Elements */}
        <HolographicUI selectedArtwork={selectedArtwork} />
        
        <AdvancedLightingSystem 
          selectedArtwork={selectedArtwork}
          cameraPosition={GALLERY_CONFIG.CAMERA_POSITION}
        />
        
        {/* Contact shadows for ground plane */}
        <ContactShadows 
          position={GALLERY_CONFIG.CONTACT_SHADOWS.POSITION}
          opacity={GALLERY_CONFIG.CONTACT_SHADOWS.OPACITY}
          scale={GALLERY_CONFIG.CONTACT_SHADOWS.SCALE}
          blur={GALLERY_CONFIG.CONTACT_SHADOWS.BLUR}
          far={GALLERY_CONFIG.CONTACT_SHADOWS.FAR}
        />
        
        {/* Render artworks in circle */}
        {mediaItems.filter(item => item.type === 'image').map((item, index) => {
          const artworkData = artworkPositions[index];
          if (!artworkData) return null;
          
          return (
            <Suspense key={item.id} fallback={null}>
              <Artwork
                position={artworkData.position}
                rotation={artworkData.rotation}
                imageSrc={item.src}
                onClick={() => {
                  handleArtworkClick(item);
                  setSelectedArtwork(item);
                }}
                isSelected={selectedArtwork?.id === item.id}
                cameraPosition={GALLERY_CONFIG.CAMERA_POSITION}
                selectedArtwork={selectedArtwork}
              />
            </Suspense>
          );
        })}
        
        {/* Camera controls for 360-degree rotation */}
        <OrbitControls
          enablePan={false}
          enableZoom={true}
          enableRotate={true}
          minDistance={GALLERY_CONFIG.ORBIT_CONTROLS.MIN_DISTANCE}
          maxDistance={GALLERY_CONFIG.ORBIT_CONTROLS.MAX_DISTANCE}
          autoRotate={true}
          autoRotateSpeed={GALLERY_CONFIG.ORBIT_CONTROLS.AUTO_ROTATE_SPEED}
        />
      </Canvas>
      
      {/* Selected artwork info overlay */}
      {selectedArtwork && (
        <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white p-4 rounded-lg max-w-sm">
          <h3 className="text-lg font-bold mb-2">{selectedArtwork.title}</h3>
          <p className="text-sm text-gray-300">{selectedArtwork.description}</p>
        </div>
      )}
      
      {/* Floating Contact Button */}
      <button
        onClick={() => setIsContactOpen(true)}
        className="fixed bottom-8 right-8 w-14 h-14 bg-black bg-opacity-80 border border-white border-opacity-30 rounded-full flex items-center justify-center text-white hover:bg-opacity-100 hover:border-opacity-80 transition-all duration-300 z-40 group"
        style={{
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
        }}
      >
        <span className="text-lg font-thin tracking-wider group-hover:scale-110 transition-transform duration-300">
          @
        </span>
        
        {/* Floating contact hint */}
        <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-black bg-opacity-90 text-white text-xs font-thin tracking-wider rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          CONTACT
        </div>
        
        {/* Pulsing ring effect */}
        <div className="absolute inset-0 rounded-full border border-white border-opacity-20 animate-ping" />
      </button>
      
      {/* Contact Form */}
      <ContactForm 
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
      />
      
      {/* Enhanced Artwork Modal with Lazy Loading */}
      {isModalOpen && (
        <Suspense fallback={
          <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center">
            <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        }>
          <EnhancedArtworkModal 
            artwork={fullscreenArtwork}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            allArtworks={mediaItems}
            onNavigate={handleArtworkNavigate}
          />
        </Suspense>
      )}
    </div>
  );
};

export default VirtualGallery;
